import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, EyeIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';

// HTML preview: render iframe at a virtual design width, then CSS-scale it
// down to the container's actual width. 960px matches common 16:9 slide
// design widths, so PPT-style pages fit without horizontal clipping.
const HTML_VIRTUAL_WIDTH = 960;
const HTML_VIRTUAL_HEIGHT = 540;

const getFileName = (filePath: string): string => {
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || filePath;
};

/** HTML iframe rendered at virtual 960×540, CSS-scaled to fit container width. */
const HtmlIframePreview: React.FC<{ content: string; title: string }> = ({ content, title }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / HTML_VIRTUAL_WIDTH);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden bg-white"
      style={{ width: '100%', aspectRatio: '16 / 9' }}
    >
      <iframe
        srcDoc={content}
        sandbox="allow-scripts"
        title={title}
        className="block border-0"
        style={{
          width: HTML_VIRTUAL_WIDTH,
          height: HTML_VIRTUAL_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
    </div>
  );
};

interface InlineFilePreviewProps {
  filePath: string;
  content: string;
}

/** Inline HTML preview rendered under a successful Write tool call. */
const InlineFilePreview: React.FC<InlineFilePreviewProps> = ({ filePath, content }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const fileName = getFileName(filePath);

  return (
    <div className="mt-2 ml-4 max-w-[640px] rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(v => !v)}
        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 dark:bg-claude-darkSurface bg-claude-surface dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
      >
        {isExpanded
          ? <ChevronDownIcon className="h-3.5 w-3.5 dark:text-claude-darkTextSecondary text-claude-textSecondary shrink-0" />
          : <ChevronRightIcon className="h-3.5 w-3.5 dark:text-claude-darkTextSecondary text-claude-textSecondary shrink-0" />}
        <EyeIcon className="h-3.5 w-3.5 text-claude-accent shrink-0" />
        <span className="text-[11px] font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary shrink-0">
          {i18nService.t('coworkDeliverablesPreview')}
        </span>
        <span className="text-[11px] dark:text-claude-darkText text-claude-text font-mono truncate flex-1 text-left">
          {fileName}
        </span>
      </button>
      {isExpanded && <HtmlIframePreview content={content} title={fileName} />}
    </div>
  );
};

export default InlineFilePreview;
